import { ArrowTrendingDownIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { type ColumnDef, DataTable } from '@oysterjs/ui/data-table';
import clsx from 'clsx';
import React from 'react';
import type {
  AdminAnalyticsPresetUsageFieldsFragment,
  AdminAnalyticsReportFieldsFragment
} from '~/api/graphql';
import { pluralize } from '~/lib/pluralize';
import {
  countLabel,
  dateFormatter,
  formatDayRange,
  formatNumber,
  utcDateFormatter
} from '~/components/settings/analytics/analytics-constants';
import {
  DeletedPresetFootnote,
  EmptyMessage,
  presetDisplayLabel,
  SectionHeading,
  TrendBars
} from './analytics-card-parts';
import { CustomPresetTag } from './collapsed-legend-popover';

type PresetRankings = AdminAnalyticsReportFieldsFragment['presetRankings'];
type PresetUsage = AdminAnalyticsPresetUsageFieldsFragment;
type PresetUser = PresetUsage['users'][number];

// One row of the Users table: a user, or the workspaces with no user attached.
type PresetUserRow = Pick<PresetUser, 'workspaceCount' | 'lastUsedAt'> & {
  key: string;
  name: string;
  detail: string;
  // The fraction of the preset's workspaces, from 0 to 1.
  share: number;
};

// Keys and labels the row for the workspaces with no user attached.
const NO_USER = 'No user';

// About 10 rows of the two-line User cell before the table scrolls.
const USER_TABLE_MAX_HEIGHT = '32rem';

const NUMBER_CELL_CLASS = 'py-1.5 px-3 text-right tabular-nums whitespace-nowrap';

// Share and Last used show only once the detail pane is wide enough for them (@sm and @xs),
// so a phone gets no sideways scroll.
const PRESET_USER_COLUMNS: ColumnDef<PresetUserRow>[] = [
  {
    id: 'user',
    header: 'User',
    // Takes the spare width and truncates, so a long email can't widen the table.
    cellClassName: 'py-1.5 px-3 max-w-0 w-full',
    renderCell: (row) => <UserCell name={row.name} detail={row.detail} />
  },
  {
    id: 'workspaces',
    header: 'Workspaces',
    headerClassName: 'text-right whitespace-nowrap',
    cellClassName: NUMBER_CELL_CLASS,
    renderCell: (row) => formatNumber(row.workspaceCount)
  },
  {
    id: 'share',
    header: 'Share',
    headerClassName: 'hidden text-right @sm:table-cell',
    cellClassName: `hidden ${NUMBER_CELL_CLASS} @sm:table-cell`,
    renderCell: (row) => `${Math.round(row.share * 100)}%`
  },
  {
    id: 'lastUsed',
    header: 'Last used',
    headerClassName: 'hidden whitespace-nowrap @xs:table-cell',
    cellClassName: 'hidden py-1.5 px-3 whitespace-nowrap @xs:table-cell',
    renderCell: (row) => dateFormatter.format(new Date(row.lastUsedAt))
  }
];

// The preset's users, most workspaces first, then the workspaces with no user attached.
function presetUserRows({
  users,
  withoutUser,
  workspaceCount: presetWorkspaceCount
}: PresetUsage): PresetUserRow[] {
  const shareOf = (workspaceCount: number) =>
    presetWorkspaceCount > 0 ? workspaceCount / presetWorkspaceCount : 0;
  const rows: PresetUserRow[] = users.map((user) => ({
    ...user,
    key: user.agencyUserId,
    name: user.agencyUserName,
    detail: user.agencyUserEmail,
    share: shareOf(user.workspaceCount)
  }));
  if (withoutUser) {
    rows.push({
      ...withoutUser,
      key: NO_USER,
      name: NO_USER,
      detail: 'From an integration, like Zapier',
      share: shareOf(withoutUser.workspaceCount)
    });
  }
  return rows;
}

export const PresetRankingsSection = ({ presetRankings }: { presetRankings: PresetRankings }) => {
  const [selectedKey, setSelectedKey] = React.useState<string>();
  const listRef = React.useRef<HTMLDivElement>(null);
  const detailRef = React.useRef<HTMLDivElement>(null);
  const { presets, noPreset, previousPeriod } = presetRankings;
  const usages = [...presets.map((p) => p.usage), ...(noPreset ? [noPreset] : [])];
  const heading = (
    <SectionHeading
      title="Preset rankings"
      subtitle="Presets ranked by the workspaces your team started from them"
    />
  );

  if (usages.length === 0) {
    return (
      <>
        {heading}
        <EmptyMessage>No workspaces in this view.</EmptyMessage>
      </>
    );
  }

  const selectedUsage = usages.find((u) => u.key === selectedKey) ?? usages[0];
  const previousPeriodDates =
    previousPeriod &&
    formatDayRange(new Date(previousPeriod.after), new Date(previousPeriod.before));
  const selectUsage = (key: string) => {
    setSelectedKey(key);
    // On narrow screens the detail stacks under the list, out of sight of the row just picked.
    const detail = detailRef.current;
    const isDetailBelowList = (detail?.offsetTop ?? 0) > (listRef.current?.offsetTop ?? 0);
    if (isDetailBelowList) {
      detail?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {heading}
      {usages.some((u) => u.customPresetArchived) && <DeletedPresetFootnote />}
      <div className="mt-4 grid gap-6 lg:grid-cols-2">
        <div ref={listRef} className="min-w-0">
          <ol className="space-y-0.5 overflow-y-auto [scrollbar-gutter:stable] lg:max-h-[28rem]">
            {presets.map(({ rank, previousRank, usage }) => (
              <li key={usage.key}>
                <PresetRow
                  usage={usage}
                  rank={rank}
                  isSelected={usage.key === selectedUsage.key}
                  onSelect={() => selectUsage(usage.key)}
                  rankChange={
                    previousPeriodDates ? (
                      <RankChangeBadge
                        rank={rank}
                        previousRank={previousRank}
                        previousPeriodDates={previousPeriodDates}
                      />
                    ) : null
                  }
                />
              </li>
            ))}
          </ol>
          {noPreset && (
            // Never scrolls; the matching gutter lines its count up with the scrolling list's.
            <div className="mt-2 overflow-y-auto border-t border-neutral-100 pt-2 [scrollbar-gutter:stable] dark:border-neutral-700">
              <PresetRow
                usage={noPreset}
                isSelected={noPreset.key === selectedUsage.key}
                onSelect={() => selectUsage(noPreset.key)}
              />
            </div>
          )}
        </div>
        {/* scroll-mt-20 clears the h-16 sticky header the page shows below lg. */}
        <div ref={detailRef} className="min-w-0 scroll-mt-20">
          <PresetDetail usage={selectedUsage} weekCount={presetRankings.weekCount} />
        </div>
      </div>
    </>
  );
};

const PresetRow = ({
  usage,
  rank,
  isSelected,
  onSelect,
  rankChange
}: {
  usage: PresetUsage;
  rank?: number;
  isSelected: boolean;
  onSelect: () => void;
  rankChange?: React.ReactNode;
}) => {
  const label = presetDisplayLabel(usage.label, usage.customPresetArchived);
  return (
    <button
      type="button"
      aria-current={isSelected ? 'true' : undefined}
      onClick={onSelect}
      className={clsx(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm',
        'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500',
        isSelected
          ? 'bg-primary-50 ring-1 ring-inset ring-primary-200 dark:bg-primary-500/10 dark:ring-primary-500/30'
          : 'hover:bg-neutral-50 dark:hover:bg-white/5'
      )}
    >
      <span className="w-8 shrink-0 tabular-nums text-neutral-500 dark:text-neutral-400">
        {rank !== undefined && `#${rank}`}
      </span>
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span className="truncate font-medium text-neutral-900 dark:text-white" title={label}>
          {label}
        </span>
        {usage.isCustomPreset && <CustomPresetTag />}
      </span>
      <span className="shrink-0 tabular-nums text-neutral-500 dark:text-neutral-400">
        {formatNumber(usage.workspaceCount)}
        <span className="hidden sm:inline">
          {` ${pluralize(usage.workspaceCount, 'workspace')}`}
        </span>
      </span>
      {rankChange}
    </button>
  );
};

const RankChangeBadge = ({
  rank,
  previousRank,
  previousPeriodDates
}: {
  rank: number;
  previousRank?: number | null;
  previousPeriodDates: string;
}) => {
  if (previousRank == null) {
    return (
      <Badge color="violet" title={`No workspaces in ${previousPeriodDates}`}>
        <span aria-hidden>New</span>
        <span className="sr-only">{`New, no workspaces in ${previousPeriodDates}`}</span>
      </Badge>
    );
  }
  const placesUp = previousRank - rank;
  if (placesUp === 0) {
    return null;
  }
  const isUp = placesUp > 0;
  const places = Math.abs(placesUp);
  const comparison = `#${previousRank} in ${previousPeriodDates}`;
  const TrendIcon = isUp ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  return (
    <Badge color={isUp ? 'emerald' : 'rose'} title={comparison}>
      <TrendIcon aria-hidden className="size-3" />
      <span aria-hidden>{places}</span>
      <span className="sr-only">
        {`${isUp ? 'Up' : 'Down'} ${places} ${pluralize(places, 'place')} from ${comparison}`}
      </span>
    </Badge>
  );
};

const PresetDetail = ({ usage, weekCount }: { usage: PresetUsage; weekCount: number }) => {
  const userCount = usage.users.length;
  const bars = usage.overTime.map((week) => {
    const periodStart = new Date(week.weekStart);
    return {
      periodStart,
      count: week.workspaceCount,
      recorded: true,
      title: `Week of ${utcDateFormatter.format(periodStart)}: ${countLabel(week.workspaceCount, 'workspace')}`
    };
  });

  return (
    <>
      <h4 className="my-0 text-sm font-semibold text-neutral-900 dark:text-white">
        {presetDisplayLabel(usage.label, usage.customPresetArchived)}
        <span className="font-normal text-neutral-500 dark:text-neutral-400">
          {` · ${countLabel(usage.workspaceCount, 'workspace')} · ${countLabel(userCount, 'user')}`}
        </span>
      </h4>
      {bars.length > 0 && (
        <div className="mt-4">
          <TrendBars bars={bars} label={`Workspaces per week, ${bars.length} weeks`} />
          {weekCount > bars.length && (
            <p className="mt-2 text-[11px] text-neutral-500 dark:text-neutral-400">
              Showing the latest {bars.length} of {weekCount} weeks
            </p>
          )}
        </div>
      )}
      <div className="mt-4 @container">
        <DataTable
          data={presetUserRows(usage)}
          columns={PRESET_USER_COLUMNS}
          keyExtractor={(row) => row.key}
          stickyHeader
          scrollable
          maxHeight={USER_TABLE_MAX_HEIGHT}
        />
      </div>
    </>
  );
};

// The minimum keeps a name and email readable, truncated, however narrow the pane.
const UserCell = ({ name, detail }: { name: string; detail: string }) => (
  <div className="min-w-[8rem]">
    <div className="truncate font-medium text-neutral-900 dark:text-white" title={name}>
      {name}
    </div>
    <div className="truncate text-xs text-neutral-500 dark:text-neutral-400" title={detail}>
      {detail}
    </div>
  </div>
);
